import _ from 'lodash';

import {
  BANK_ACCOUNT_UID,
  CLEARING_ACCOUNT_BALANCING_QUESTION_UID,
  CLEARING_ACCOUNT_OPTION_QUESTION_UID,
  CLEARING_ACCOUNT_QUESTION_UID,
  FIELD_DOES_NOT_FITT_MSG,
  NOTES_FOR_ORIGIN_OF_ASSETS_UID,
  ORIGIN_OF_ASSETS_NEW_UID,
  ORIGIN_OF_ASSETS_UID,
  QUESTION_TYPE
} from "../../constants";
import {validateMultilineContent} from "../../utils";
import AssetsOriginStep from "../StepContent/components/step/CustomerDataSteps/AssetsOriginStep";
import DepotAccountStepV2 from "../StepContent/components/step/CustomerDataSteps/DepotAccountStepV2";
import {NOT_ALLOWED_BANKS} from "../StepContent/components/step/CustomerDataSteps/constants";

/**
 * Generate a unique step ID for a depot account step.
 * @param {Object} portfolio Portfolio object with portfolioId
 * @returns {string} Unique step UID
 */
export const getDepotAccountStepUID = (portfolio) =>
  `ref_account_depot_${portfolio.portfolioId}`;

/**
 * Build a single depot account step for one portfolio.
 * Contains clearing account questions and reference account selection scoped to this depot.
 *
 * @param {Object} portfolio Portfolio object
 * @returns {Object} Step definition
 */
const buildDepotAccountStep = (portfolio) => ({
  "uid": getDepotAccountStepUID(portfolio),
  "parent": "Abwicklung",
  "category": _.get(portfolio, 'data.name'),
  "behaviour": ["dynamic"],
  "skippable": true,
  "step_content": DepotAccountStepV2,
  "portfolioId": portfolio.portfolioId,
  "question": [
    {
      "uid": CLEARING_ACCOUNT_QUESTION_UID,
      "custom_classes": {"labelRoot": "bold"},
      "optional": true,
      "question": [] // will be filled with clearing account checkbox for this depot
    },
    {
      "uid": CLEARING_ACCOUNT_OPTION_QUESTION_UID,
      "optional": true,
      "question": [] // will be filled with clearing account select for this depot
    },
    {
      "uid": CLEARING_ACCOUNT_BALANCING_QUESTION_UID,
      "optional": true,
      "question": [] // will be filled with balancing confirmation checkbox for this depot
    },
    {
      "uid": BANK_ACCOUNT_UID,
      "type": QUESTION_TYPE.REFERENCE_ACCOUNTS_V2,
      "question_text": "Referenzkonto",
      "bank_accounts": [],
      "optional": false,
      "bank_warn": NOT_ALLOWED_BANKS[portfolio.bankCode],
    }
  ]
});

/**
 * Build per-depot account steps for Trading V2.
 * Creates one step per portfolio for cash account / reference account selection.
 *
 * @param {Array} portfolios List of portfolio objects from trading step
 * @param {Array} originOfAssetsOptions Options for origin of assets
 * @returns {{steps: Array}} Object with steps array
 */
export const buildDepotAccountSteps = (portfolios, originOfAssetsOptions) => {
  const steps = [];

  // Create one step per depot
  portfolios.forEach(portfolio => {
    steps.push(buildDepotAccountStep(portfolio));
  });

  // Origin of assets as a shared step (not per-depot)
  if (!_.isEmpty(originOfAssetsOptions)) {
    steps.push({
      "uid": ORIGIN_OF_ASSETS_UID,
      "name": "Herkunft der Vermögenswerte *",
      "category": "Herkunft der Vermögenswerte",
      "behaviour": ["dynamic"],
      "skippable": true,
      "step_content": AssetsOriginStep,
      "question": [
        {
          "uid": ORIGIN_OF_ASSETS_NEW_UID,
          "question_info": "Bitte beachten Sie, dass die Depotstelle gegebenfalls Belege zur Herkunft des Vermögens einfordern.",
          "type": QUESTION_TYPE.CHECKBOX_LIST_CARDS,
          "optional": true,
          "required_check": true,
          "config": {
            "store_choices": true,
            "choices": originOfAssetsOptions.map(option => ({
              "id": option.id,
              "uid": option.id,
              "text": option.label,
            }))
          }
        },
        {
          "uid": NOTES_FOR_ORIGIN_OF_ASSETS_UID,
          "question_text": "Anmerkungen zur Herkunft der Vermögenswerte",
          "question_info": "Sofern insbesondere risikobasiert eine Erklärung und/oder Erläuterung zur Herkunft der Vermögenswerte erfolgen soll, tragen Sie diese oder sonstige Anmerkungen bitte hier ein.",
          "type": "input",
          "config": {
            "multiline": true
          },
          "validateImmediately": true,
          "isValid": function () {
            let isValid = true;
            this.error = null;

            if (this.answer) {
              isValid = validateMultilineContent(this.answer, 100);
              this.error = isValid ? null : FIELD_DOES_NOT_FITT_MSG
            }

            return isValid
          }
        }
      ]
    });
  }

  return {
    steps: steps
  }
};
